import type { ReactNode } from "react";
import { Button, Select, vars } from "@pack-it-up/ui-web";
import type { ListViewConfig } from "@pack-it-up/core";
import type { UserId } from "@pack-it-up/shared";
import { SearchField } from "./SearchField.js";

// Kit `.list-toolbar`: a muted summary on the left ("38 items across 4 categories"),
// then a right-aligned `.toolbar-group` of compact controls — sort/filter selects and
// the list's bulk action (e.g. the danger Clear checked). "Done last" moved to Trip
// Settings (trip-wide, not per-list). Emits partial `ListViewConfig` patches to
// `tripDetailStore.setItemsView`; the list renders `visibleItems()` (Doc 05 §7).
export const Toolbar = ({
  view,
  onChange,
  summary,
  statusLabels = { incomplete: "To pack", complete: "Packed" },
  searchPlaceholder = "Search this list…",
  assignees,
  tags,
  onRenameTag,
  trailing,
  includeDueDateSort = true,
}: {
  view: ListViewConfig;
  onChange: (patch: Partial<ListViewConfig>) => void;
  /** Left-hand muted list summary, e.g. "38 items across 4 categories". */
  summary?: string;
  /** Placeholder for the search box (e.g. "Search items…"). */
  searchPlaceholder?: string;
  // Status-filter wording differs per list (packing vs to-do).
  statusLabels?: { incomplete: string; complete: string };
  // Participant options for the assignee filter (packing only). Omitted → no filter.
  assignees?: { value: UserId; label: string }[];
  // Tags in use on this list (T29). The filter is single-select for now; the config
  // models it as an array (OR semantics) so multi-select needs no core change.
  tags?: string[];
  // Issue #181 — opens the rename dialog for the tag currently being filtered on.
  // Omitted (or no tag selected) → no affordance.
  onRenameTag?: (tag: string) => void;
  /** Right-most toolbar action (e.g. the danger Clear checked button). */
  trailing?: ReactNode;
  // Due-date sort only means anything for to-dos — packing items have no due date, so
  // `applyView` falls through to manual order for them anyway. Packing's Toolbar omits it.
  includeDueDateSort?: boolean;
}): ReactNode => {
  // Single-select assignee filter over the `UserId[] | "all"` model.
  const assigneeValue = view.filterAssignees === "all" ? "all" : (view.filterAssignees[0] ?? "all");
  const tagValue = view.filterTags[0] ?? "all";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: vars.space.sm, flexWrap: "wrap", marginBottom: vars.space.md }}>
      <SearchField
        value={view.query}
        onChange={(query) => onChange({ query })}
        placeholder={searchPlaceholder}
      />
      {summary !== undefined && <span style={{ fontSize: vars.font.sizeXs, color: vars.color.textMuted }}>{summary}</span>}
      <div style={{ display: "flex", alignItems: "center", gap: "6px", marginLeft: "auto", flexWrap: "wrap" }}>
        <Select
          compact
          label="Sort"
          value={view.sort}
          onChange={(v) => onChange({ sort: v as ListViewConfig["sort"] })}
          options={[
            { value: "manual", label: "Sort: Manual" },
            { value: "alphabetical", label: "Sort: A–Z" },
            ...(includeDueDateSort ? [{ value: "dueDate", label: "Sort: Due date" }] : []),
          ]}
        />
        <Select
          compact
          label="Show"
          value={view.filterStatus}
          onChange={(v) => onChange({ filterStatus: v as ListViewConfig["filterStatus"] })}
          options={[
            { value: "all", label: "Show: All" },
            { value: "incomplete", label: `Show: ${statusLabels.incomplete}` },
            { value: "complete", label: `Show: ${statusLabels.complete}` },
          ]}
        />
        {assignees !== undefined && (
          <Select
            compact
            label="Assignee"
            value={assigneeValue}
            onChange={(v) => onChange({ filterAssignees: v === "all" ? "all" : [v as UserId] })}
            options={[{ value: "all", label: "Everyone" }, ...assignees]}
          />
        )}
        {tags !== undefined && tags.length > 0 && (
          <Select
            compact
            label="Tag"
            value={tagValue}
            onChange={(v) => onChange({ filterTags: v === "all" ? [] : [v] })}
            options={[{ value: "all", label: "Tag: All" }, ...tags.map((t) => ({ value: t, label: `Tag: ${t}` }))]}
          />
        )}
        {onRenameTag !== undefined && tagValue !== "all" && (
          <Button variant="ghost" size="sm" onClick={() => onRenameTag(tagValue)}>
            Rename tag
          </Button>
        )}
        {trailing}
      </div>
    </div>
  );
};
